// Territory arena constants (docs/territory-arena.md; numbers from docs/parity/territory.md §9,
// owner decisions in docs/parity/territory-backlog.md "Owner decisions (2026-10-31)").
//
// - `ARENA_DEFAULTS` mirrors config/default.json "arena" key for key (test/constants.test.ts
//   enforces it, like the other *_DEFAULTS). The server reads the real values from config; the
//   client gets the ones it needs from the WELCOME arena block (protocol §5.3). Nobody hard-codes
//   them (change order lane R).
// - `ARENA_NET` are wire facts (chunk size, fixed-point steps, cadences): not config.
// - `ARENA_CLIENT` are client timing and look constants (territory.md §9, visuals, UI): not config.
//
// Contracts step (2026-10-31). Owners: lane P (this file, the protocol), lane S (sim numbers),
// lane C (ARENA_CLIENT), lane B (bots block).

import { deepFreeze } from '../constants.js';

// ---------------------------------------------------------------------------------------------
// Rule switches (owner decisions)
// ---------------------------------------------------------------------------------------------

/** `arena.world.shape`: the reference's circle (territory.md §2) or the square fallback. */
export const ARENA_WORLD_SHAPES = ['circle', 'square'] as const;
export type ArenaWorldShape = (typeof ARENA_WORLD_SHAPES)[number];

/**
 * `arena.capture.siege` (D-T1). Who dies when a capture changes the owner of cells:
 * - `exitPoint` (owner's choice, the reference rule): an enemy at home dies when its head cell
 *   changed owner; an enemy outside its land dies when its trail's exit point (first trail cell)
 *   changed owner. A sortie head inside the loop whose exit point is outside survives.
 * - `spec`: the original spec §4 rule, any enemy head on a cell that changed owner dies.
 * Both credit the capturer with the whole bag.
 */
export const ARENA_SIEGE_RULES = ['exitPoint', 'spec'] as const;
export type ArenaSiegeRule = (typeof ARENA_SIEGE_RULES)[number];

/**
 * `arena.capture.severed` (D-T2). What happens to an enemy's land that a capture cuts into
 * several pieces:
 * - `neutral` (owner's choice, the reference rule): the enemy keeps only the piece holding its
 *   head (at home) or its trail's exit point (out); every other piece becomes neutral.
 * - `keep`: the original spec, every piece survives; starvation at 0 cells still applies.
 */
export const ARENA_SEVERED_RULES = ['neutral', 'keep'] as const;
export type ArenaSeveredRule = (typeof ARENA_SEVERED_RULES)[number];

/** Why an arena life died (spec §5, backlog M-1). `exitCaptured` exists with siege `exitPoint`. */
export const ARENA_DEATH_CAUSES = ['cut', 'enclosed', 'starved', 'self', 'exitCaptured'] as const;
export type ArenaDeathCause = (typeof ARENA_DEATH_CAUSES)[number];

// ---------------------------------------------------------------------------------------------
// Config defaults: config/default.json "arena"
// ---------------------------------------------------------------------------------------------

/**
 * `arena.*` (spec §16 with the territory.md §9 recommendations, scale option A). All distances
 * are in cells (1 cell = 1 world unit in arena rooms), times in seconds, angles in degrees.
 */
export const ARENA_DEFAULTS = deepFreeze({
  /** 326 × 326 grid with a Ø 325 circle mask ≈ 83,000 playable cells (2,770 per seat × 30). */
  world: { shape: 'circle' as ArenaWorldShape, cols: 326, rows: 326 },
  /** 15 cells/s (1.5 start-blob diameters per second), 360 °/s (14.4° per 40 ms tick). */
  move: { speed: 15, turnRateDeg: 360 },
  /** D-T1 and D-T2, set to the owner's choices (2026-10-31). */
  capture: {
    siege: 'exitPoint' as ArenaSiegeRule,
    severed: 'neutral' as ArenaSeveredRule,
  },
  spawn: {
    /** Disc of cells whose centre is within 5.0 of the blob centre (≈ 79–81 cells). */
    blobRadius: 5,
    /** Blob edge to the nearest owned land. */
    landMargin: 10,
    /** Blob edge to the nearest trail cell. */
    trailMargin: 20,
    /** Blob edge to the rim (out-of-mask cells). */
    borderMargin: 10,
    /** Blob centre to the nearest enemy head. */
    headMargin: 25,
    tries: 64,
    /** Capture shield; ends early at the first capture (spec §6). */
    shieldSec: 10,
  },
  /** Self-cut spill (spec §7): ceil(valueUsd / usdPerDrop) drops, clamped to 1..maxDrops. */
  drops: { usdPerDrop: 5, maxDrops: 8 },
  /** Disconnect freeze before the forced exit (spec §9). */
  exit: { graceSec: 15 },
  /** Land decay (spec §10), off. */
  decay: { enabled: false, afterSec: 90, cellsPerSec: 2 },
  /**
   * Camera (territory.md §6): a constant visible world diagonal at start (replaces the spec's
   * cellsAcross / cellsAcrossTouch), widened by land up to × zoomOutMax.
   */
  view: { cellsDiagonal: 58, zoomOutMax: 1.5 },
  /** Practice bots (territory.md §7; lane B). Money rooms never have bots. */
  bots: {
    /** A new practice room fast-forwards this much game time before its first human (B-4). */
    prewarmSec: 300,
    /** Difficulty from the richest human's land share, linear min → max, ± jitter per bot. */
    difficultyMin: 0.1,
    difficultyMax: 1,
    difficultyJitter: 0.1,
    /** Turn rate when calm / when threatened or within rimCells of the rim (B-3). */
    turnCalmDeg: 180,
    turnThreatDeg: 360,
    rimCells: 10,
    /** Sortie start: this share straight out, the rest from an edge point ≥ edgeMinCells away. */
    sortieStraightShare: 0.25,
    sortieEdgeMinCells: 15,
    /** The bots' "view" radius for the sortie-size limits. */
    viewCells: 23,
    /** Hunt human trails within min..max cells (aggro-scaled); long trails pull the nearest bot. */
    huntMinCells: 3.5,
    huntMaxCells: 17,
    huntLongTrailCells: 250,
    /** Break off a hunt when danger > breakOffDanger × defence. */
    breakOffDanger: 0.8,
    /** Respawn this far from a human when the margins allow (B-5). */
    spawnNearMinCells: 60,
    spawnNearMaxCells: 80,
  },
});
export type ArenaDefaults = typeof ARENA_DEFAULTS;

// ---------------------------------------------------------------------------------------------
// Wire facts (protocol §5.4 arena sections)
// ---------------------------------------------------------------------------------------------

export const ARENA_NET = deepFreeze({
  /** Land chunks are 16 × 16 cells, row-major inside the chunk; chunk index = cy · chunksX + cx. */
  chunkSize: 16,
  /** Head positions are sent as `vu round(x · 256)`: 1/256 cell (backlog P-2). */
  posSteps: 256,
  /** ARENA_INPUT heading: `u16`, 65,536 steps per turn (≥ 256 required by backlog P-1). */
  headingSteps: 65_536,
  /** ARENA_MINIMAP: the ownership map downsampled to 50 × 50, every 25 ticks (spec §11). */
  minimapSize: 50,
  minimapEveryTicks: 25,
  /** Heads listed on the minimap besides your own (spec §11: the top 20). */
  minimapTopHeads: 20,
  /** LEADERBOARD in arena rooms: 5 Hz (backlog P-5). */
  leaderboardEveryTicks: 5,
  /** Leaderboard rows in arena rooms: top 5 (+ your own rank and amount). */
  leaderboardSize: 5,
});

/** Count limits of arena list fields (decoders reject more). */
export const MAX_ARENA_HEADS = 1024;
export const MAX_ARENA_CHUNKS = 4096;
export const MAX_ARENA_TRAIL_RECORDS = 1024;
export const MAX_ARENA_TRAIL_STEPS = 65_535;
export const MAX_ARENA_DROPS = 4096;
export const MAX_ARENA_EVENTS = 1024;

// ---------------------------------------------------------------------------------------------
// Client constants (territory.md §9, visuals, UI): timing and look, never config
// ---------------------------------------------------------------------------------------------

export const ARENA_CLIENT = deepFreeze({
  /** Zoom-out by land in 5 % steps (easeOutCubic up to WELCOME zoomOutMax), τ 400 ms. */
  zoomStep: 0.05,
  zoomTauMs: 400,
  /** After death the camera pans to the killer, frame-rate independent half-life. */
  deathPanHalfLifeMs: 340,
  /** Spectator view diagonal = cellsDiagonal × this. */
  spectatorDiagonalScale: 1.8,
  /** Results open this long after the death: own fault (self, wall) / killed (C-8). */
  resultsDelaySelfMs: 1000,
  resultsDelayKilledMs: 2000,
  /** Cash-out and forced-exit results open after this. */
  resultsDelayExitMs: 600,
  /** Floating labels (+x.xx %, kill, +$) last this long (C-12). */
  labelMs: 1000,
  /** "+x.xx%" only for gains of at least this many percent. */
  captureLabelMinPct: 0.01,
  /** Own-head prediction: corrections blend over this long (C-2). */
  correctionBlendMs: 100,
  /** Drawn head and trail width in cells (the sim's are 1 cell; territory.md §0 option A). */
  headDrawCells: 1.33,
  trailDrawCells: 1.33,
  /** Land and head tops are raised this much; side shade V × 0.75, base shade V × 0.5. */
  liftCells: 0.33,
  sideShade: 0.75,
  baseShade: 0.5,
  trailAlpha: 0.6,
  /** Own-capture flash (C-20); 0 turns it off. */
  captureFlashMs: 90,
  captureFlashAlpha: 0.12,
  /** Touch stick (C-10): dead zone, anchor follow distance, hold button size (CSS px). */
  stickDeadZonePx: 10,
  stickFollowPx: 60,
  holdButtonPx: 72,
  /** Cosmetic events older than this never play (sounds, labels, bursts; C-15, C-23). */
  staleEventMs: 500,
  /** Kill feed: lines shown and how long each stays (C-17). */
  killFeedLines: 4,
  killFeedMs: 4000,
  /** Scene colours (D-V2: the measured scene colours are kept). Player colours are our own. */
  colors: {
    ground: 0xebf8f1,
    rimLip: 0x8ca597,
    outsideTop: 0x2a6191,
    outsideBottom: 0x7ce8f1,
    minimapArena: 0xc6d8cf,
    minimapAlarm: 0xff3b30,
    labelFill: 0xe4e4e4,
    labelShadow: 0x33302e,
    bag: 0x7cf29a,
    crownFill: 0xffd54a,
    crownStroke: 0xd98a00,
  },
});
